import { useMemo, useState } from "react";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

export type SectorOutlookSource = {
  id: string;
  title: string;
  url: string;
};

export type SectorIndustryOutlookData = {
  sectorName?: string | null;
  sectorRisk?: string | null;
  marketSize?: string | null;
  industryCagr?: string | null;
  marketGrowth?: string | null;
  summary?: string[];
  growthDrivers?: string[];
  keyRisks?: string[];
  sources?: SectorOutlookSource[];
  aiGenerated?: boolean;
};

const SUMMARY_PREVIEW_COUNT = 2;
const SOURCES_PREVIEW_COUNT = 1;

function riskBadgeVariant(
  risk?: string | null
): "orange" | "success" | "destructive" | "secondary" {
  const value = (risk || "").toLowerCase();
  if (value.includes("low")) return "success";
  if (value.includes("high") || value.includes("severe")) return "destructive";
  if (value.includes("medium") || value.includes("moderate")) return "orange";
  return "secondary";
}

function MetricCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1.5 text-sm font-semibold text-foreground">{children}</div>
    </div>
  );
}

export function SectorIndustryOutlookCard({
  data,
  className,
  onGenerateAI,
  isGeneratingAI = false,
  canGenerateAI = true,
}: {
  data: SectorIndustryOutlookData;
  className?: string;
  onGenerateAI?: () => void | Promise<void>;
  isGeneratingAI?: boolean;
  canGenerateAI?: boolean;
}) {
  const [showAllSummary, setShowAllSummary] = useState(false);
  const [showAllSources, setShowAllSources] = useState(false);

  const summary = data.summary?.filter(Boolean) ?? [];
  const growthDrivers = data.growthDrivers?.filter(Boolean) ?? [];
  const keyRisks = data.keyRisks?.filter(Boolean) ?? [];
  const sources = data.sources ?? [];

  const visibleSummary = showAllSummary
    ? summary
    : summary.slice(0, SUMMARY_PREVIEW_COUNT);
  const hasMoreSummary = summary.length > SUMMARY_PREVIEW_COUNT;

  const visibleSources = showAllSources
    ? sources
    : sources.slice(0, SOURCES_PREVIEW_COUNT);
  const hiddenSourceCount = Math.max(0, sources.length - SOURCES_PREVIEW_COUNT);

  const metrics = useMemo(
    () =>
      [
        data.sectorRisk
          ? {
              key: "risk",
              label: "Sector Risk",
              node: (
                <Badge
                  variant={riskBadgeVariant(data.sectorRisk)}
                  className="font-medium"
                >
                  {data.sectorRisk}
                </Badge>
              ),
            }
          : null,
        data.marketSize
          ? {
              key: "size",
              label: "Market Size",
              node: <span>{data.marketSize}</span>,
            }
          : null,
        data.industryCagr
          ? {
              key: "cagr",
              label: "Industry CAGR",
              node: <span>{data.industryCagr}</span>,
            }
          : null,
        data.marketGrowth
          ? {
              key: "growth",
              label: "Market Growth",
              node: <span>{data.marketGrowth}</span>,
            }
          : null,
      ].filter(Boolean) as Array<{
        key: string;
        label: string;
        node: React.ReactNode;
      }>,
    [data.sectorRisk, data.marketSize, data.industryCagr, data.marketGrowth]
  );

  const hasDriversOrRisks = growthDrivers.length > 0 || keyRisks.length > 0;
  const showAiAction = Boolean(onGenerateAI) || data.aiGenerated !== false;

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-white p-4 sm:p-5",
        className
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="text-base font-semibold text-foreground">
            Sector/Industry Outlook
          </h3>
          {data.sectorName ? (
            <>
              <span className="hidden text-muted-foreground sm:inline">|</span>
              <p className="text-sm text-muted-foreground">{data.sectorName}</p>
            </>
          ) : null}
        </div>
        {showAiAction && (
          <button
            type="button"
            onClick={() => {
              if (!onGenerateAI || isGeneratingAI || !canGenerateAI) return;
              void onGenerateAI();
            }}
            disabled={!onGenerateAI || isGeneratingAI || !canGenerateAI}
            className={cn(
              "inline-flex items-center gap-1.5 text-xs font-medium transition-colors",
              onGenerateAI && canGenerateAI && !isGeneratingAI
                ? "cursor-pointer text-violet-600 hover:text-violet-700"
                : "cursor-default text-muted-foreground",
              (!onGenerateAI || !canGenerateAI) && "opacity-70"
            )}
            title={
              !canGenerateAI
                ? "Industry data can be refreshed only after 3 months"
                : data.aiGenerated
                  ? "AI-generated industry outlook"
                  : "Generate industry outlook with AI"
            }
          >
            {isGeneratingAI ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-500" />
            ) : (
              <Sparkles className="h-3.5 w-3.5 text-violet-500" />
            )}
            {isGeneratingAI
              ? "Generating…"
              : data.aiGenerated
                ? "AI Generated"
                : "Generate With AI"}
          </button>
        )}
      </div>

      {metrics.length > 0 && (
        <div
          className={cn(
            "mt-4 grid gap-4 border-b border-border pb-4",
            metrics.length === 1 && "grid-cols-1",
            metrics.length === 2 && "grid-cols-1 sm:grid-cols-2",
            metrics.length === 3 && "grid-cols-1 sm:grid-cols-3",
            metrics.length >= 4 && "grid-cols-2 lg:grid-cols-4"
          )}
        >
          {metrics.map((metric) => (
            <MetricCell key={metric.key} label={metric.label}>
              {metric.node}
            </MetricCell>
          ))}
        </div>
      )}

      {summary.length > 0 && (
        <div className="relative mt-4">
          <ul className="list-disc space-y-2.5 pl-5 marker:text-foreground">
            {visibleSummary.map((item, index) => (
              <li
                key={`${index}-${item.slice(0, 24)}`}
                className="text-sm leading-relaxed text-foreground/90"
              >
                {item}
              </li>
            ))}
          </ul>

          {!showAllSummary && hasMoreSummary && (
            <>
              <div className="pointer-events-none absolute inset-x-0 bottom-10 h-16 bg-gradient-to-t from-white to-transparent" />
              <div className="mt-3 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAllSummary(true)}
                  className="h-9 w-full rounded-full border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                >
                  See more
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </>
          )}

          {showAllSummary && hasMoreSummary && (
            <div className="mt-3 flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAllSummary(false)}
                className="h-9 w-full rounded-full border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
              >
                See less
                <ChevronUp className="ml-1 h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {hasDriversOrRisks && (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          {growthDrivers.length > 0 && (
            <div className="rounded-xl border border-border p-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <p className="text-sm font-semibold">Growth Drivers:</p>
              </div>
              <ul className="list-disc space-y-2 pl-5 marker:text-foreground">
                {growthDrivers.map((item, index) => (
                  <li
                    key={`driver-${index}`}
                    className="text-sm leading-relaxed text-foreground/90"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {keyRisks.length > 0 && (
            <div className="rounded-xl border border-border p-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white">
                  <AlertCircle className="h-3.5 w-3.5" />
                </span>
                <p className="text-sm font-semibold">Key Risks</p>
              </div>
              <ul className="list-disc space-y-2 pl-5 marker:text-foreground">
                {keyRisks.map((item, index) => (
                  <li
                    key={`risk-${index}`}
                    className="text-sm leading-relaxed text-foreground/90"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {sources.length > 0 && (
        <div className="mt-4 border-t border-border pt-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
            <span className="shrink-0 text-muted-foreground">Sources:</span>
            <div className="min-w-0 flex-1 space-y-1">
              {visibleSources.map((source) => (
                <a
                  key={source.id}
                  href={source.url || undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-foreground underline decoration-foreground/30 underline-offset-2 hover:text-violet-700 hover:decoration-violet-400"
                >
                  {source.title}
                </a>
              ))}
              {!showAllSources && hiddenSourceCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllSources(true)}
                  className="text-sm font-medium text-violet-600 hover:text-violet-700"
                >
                  +{hiddenSourceCount} more
                </button>
              )}
              {showAllSources && hiddenSourceCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllSources(false)}
                  className="text-sm font-medium text-violet-600 hover:text-violet-700"
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function asStringList(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          const record = item as Record<string, unknown>;
          const text =
            record.text ??
            record.point ??
            record.description ??
            record.summary ??
            record.name;
          return typeof text === "string" ? text.trim() : "";
        }
        return "";
      })
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/\n|•/g)
      .map((part) => part.replace(/^[-*]\s*/, "").trim())
      .filter(Boolean);
  }
  return [];
}

/** Supports plain string, `{ value }`, or Python-dict-like Market Size strings. */
function asMetricValue(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "string" && value.trim()) {
    const trimmed = value.trim();
    // e.g. "{'current': 'USD 155 Bn', 'forecast_2030': 'USD 230 Bn'}"
    if (trimmed.startsWith("{") && trimmed.includes(":")) {
      try {
        const parsed = JSON.parse(trimmed.replace(/'/g, '"')) as Record<
          string,
          unknown
        >;
        const current =
          typeof parsed.current === "string" ? parsed.current.trim() : "";
        const forecast =
          typeof parsed.forecast_2030 === "string"
            ? parsed.forecast_2030.trim()
            : typeof parsed.forecast === "string"
              ? parsed.forecast.trim()
              : "";
        if (current && forecast) return `${current} → ${forecast}`;
        if (current) return current;
        if (typeof parsed.value === "string" && parsed.value.trim()) {
          return parsed.value.trim();
        }
      } catch {
        // fall through to raw string
      }
    }
    return trimmed;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.value === "string" && record.value.trim()) {
      return record.value.trim();
    }
    const current =
      typeof record.current === "string" ? record.current.trim() : "";
    const forecast =
      typeof record.forecast_2030 === "string"
        ? record.forecast_2030.trim()
        : typeof record.forecast === "string"
          ? record.forecast.trim()
          : "";
    if (current && forecast) return `${current} → ${forecast}`;
    if (current) return current;
    if (typeof record.label === "string" && record.label.trim()) {
      return record.label.trim();
    }
    if (typeof record.text === "string" && record.text.trim()) {
      return record.text.trim();
    }
  }
  return null;
}

/** "Medium — rationale..." → "Medium" for the risk badge. */
function asSectorRiskLabel(value: unknown): string | null {
  const raw = asMetricValue(value);
  if (!raw) return null;
  const match = raw.match(/^(Low|Medium|High|Moderate|Severe)\b/i);
  return match ? match[1] : raw;
}

function asSources(value: unknown): SectorOutlookSource[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const title =
        (typeof record.heading === "string" && record.heading) ||
        (typeof record.title === "string" && record.title) ||
        (typeof record.name === "string" && record.name) ||
        (typeof record.label === "string" && record.label) ||
        "";
      const url =
        (typeof record.link === "string" && record.link) ||
        (typeof record.url === "string" && record.url) ||
        "#";
      if (!title) return null;
      return {
        id: String(record.index ?? record.id ?? url ?? index),
        title,
        url,
      };
    })
    .filter(Boolean) as SectorOutlookSource[];
}

function firstNonEmptyList<T>(...lists: T[][]): T[] {
  return lists.find((list) => list.length > 0) ?? [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function field(
  obj: Record<string, unknown>,
  ...keys: string[]
): unknown {
  for (const key of keys) {
    if (obj[key] != null && obj[key] !== "") return obj[key];
  }
  return undefined;
}

function isOutlookShape(obj: Record<string, unknown>): boolean {
  return (
    obj.sector_risk != null ||
    obj.sectorRisk != null ||
    obj["Sector Risk"] != null ||
    obj.market_size != null ||
    obj.marketSize != null ||
    obj["Market Size"] != null ||
    obj.industry_cagr != null ||
    obj["Industry CAGR"] != null ||
    obj.overview != null ||
    obj.growth_drivers != null ||
    obj.growthDrivers != null ||
    obj["Growth Drivers"] != null ||
    obj.key_risks != null ||
    obj.keyRisks != null ||
    obj["Key Risks"] != null ||
    obj.summary != null ||
    obj["Market Growth"] != null
  );
}

/** Unwrap API wrappers like `{ industryOutlook: {...} }` down to the outlook block. */
function pickOutlookBlock(
  root: Record<string, unknown>
): Record<string, unknown> {
  const candidates: unknown[] = [
    root.industryOutlook,
    root.industry_outlook,
    root.outlook,
    root.sectorOutlook,
    root.sector_outlook,
    root.data,
    root,
  ];

  for (const candidate of candidates) {
    const rec = asRecord(candidate);
    if (!rec) continue;

    const nested =
      asRecord(rec.industryOutlook) ??
      asRecord(rec.industry_outlook) ??
      asRecord(rec.outlook);
    if (nested && isOutlookShape(nested)) return nested;
    if (isOutlookShape(rec)) return rec;
  }

  return root;
}

/**
 * Normalize API payloads into the Sector Outlook card shape.
 * Supports:
 * - `{ industryOutlook: { "Sector Risk", "Market Size", overview, "Growth Drivers", "Key Risks", sources: [{heading,link}] } }`
 * - Older `{ outlook: { sector_risk, market_size: { value }, growth_drivers, ... }, sources }`
 */
export function normalizeSectorIndustryOutlook(
  raw: unknown,
  options?: {
    sectorName?: string | null;
    fallbackSources?: SectorOutlookSource[];
  }
): SectorIndustryOutlookData | null {
  if (!raw && !options?.fallbackSources?.length && !options?.sectorName) {
    return null;
  }

  const root = asRecord(raw) ?? {};
  // Response may be `{ data: { industryOutlook, customerInfo } }` or the data object itself.
  const dataLayer = asRecord(root.data) ?? root;
  const outlook = pickOutlookBlock(dataLayer);

  const customerInfo =
    asRecord(dataLayer.customerInfo) ?? asRecord(root.customerInfo);

  const sources = firstNonEmptyList(
    asSources(outlook.sources),
    asSources(dataLayer.sources),
    asSources(root.sources),
    asSources(dataLayer.industryNews),
    asSources(root.industryNews),
    asSources(Array.isArray(raw) ? raw : null),
    options?.fallbackSources ?? []
  );

  const recentTrends = firstNonEmptyList(
    asStringList(outlook.recent_trends),
    asStringList(outlook.recentTrends),
    asStringList(field(outlook, "Recent Trends"))
  );
  const overviewBullets = firstNonEmptyList(
    asStringList(outlook.overview),
    asStringList(outlook.summary),
    asStringList(outlook.highlights),
    asStringList(field(outlook, "Overview"))
  );
  const mergedSummary =
    overviewBullets.length > 0 && recentTrends.length > 0
      ? [...overviewBullets, ...recentTrends]
      : firstNonEmptyList(overviewBullets, recentTrends);

  const growthDrivers = firstNonEmptyList(
    asStringList(field(outlook, "Growth Drivers")),
    asStringList(outlook.growth_drivers),
    asStringList(outlook.growthDrivers),
    asStringList(outlook.drivers)
  );

  const keyRisks = firstNonEmptyList(
    asStringList(field(outlook, "Key Risks")),
    asStringList(outlook.key_risks),
    asStringList(outlook.keyRisks),
    asStringList(outlook.risks)
  );

  const sectorName =
    asMetricValue(customerInfo?.industry) ||
    asMetricValue(customerInfo?.segment) ||
    asMetricValue(outlook.sectorName) ||
    asMetricValue(outlook.sector) ||
    asMetricValue(outlook.industry) ||
    asMetricValue(outlook.industryName) ||
    asMetricValue(outlook.industry_name) ||
    asMetricValue(dataLayer.industry) ||
    options?.sectorName ||
    null;

  const sectorRisk = asSectorRiskLabel(
    field(outlook, "Sector Risk", "sector_risk", "sectorRisk", "risk", "riskLevel")
  );

  const marketSize = asMetricValue(
    field(outlook, "Market Size", "market_size", "marketSize")
  );

  const industryCagr = asMetricValue(
    field(outlook, "Industry CAGR", "industry_cagr", "industryCagr", "cagr")
  );

  const marketGrowth = asMetricValue(
    field(
      outlook,
      "Market Growth",
      "market_growth",
      "marketGrowth",
      "projectedMarketSize"
    )
  );

  const hasContent =
    Boolean(sectorName) ||
    Boolean(sectorRisk) ||
    Boolean(marketSize) ||
    Boolean(industryCagr) ||
    Boolean(marketGrowth) ||
    mergedSummary.length > 0 ||
    growthDrivers.length > 0 ||
    keyRisks.length > 0 ||
    sources.length > 0;

  if (!hasContent) return null;

  const legacySummary =
    mergedSummary.length > 0
      ? mergedSummary
      : sources.length > 0 &&
          !sectorRisk &&
          !marketSize &&
          !industryCagr &&
          !marketGrowth &&
          growthDrivers.length === 0 &&
          keyRisks.length === 0
        ? sources.map((s) => s.title).filter(Boolean)
        : mergedSummary;

  const aiGenerated =
    dataLayer.ai_generated === true ||
    dataLayer.aiGenerated === true ||
    outlook.ai_generated === true ||
    outlook.aiGenerated === true ||
    Boolean(outlook.generated_at || dataLayer.generated_at) ||
    outlook.status === "success";

  return {
    sectorName,
    sectorRisk,
    marketSize,
    industryCagr,
    marketGrowth,
    summary: legacySummary,
    growthDrivers,
    keyRisks,
    sources,
    aiGenerated,
  };
}


